import axios from 'axios';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MdDelete, MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { Tooltip } from 'react-tooltip';
import { RiPencilFill } from 'react-icons/ri';
import { ToastContainer, toast } from 'react-toastify';
import ReactPaginate from 'react-paginate';

import constant from '../../utils/Constant';
import Loader from '../partials/Loader';
import config from '../../config/config';

const {
    ALL_SERVICE_END_POINT,
    BOOLEAN_TRUE: trueValue,
    BOOLEAN_FALSE: falseValue,
    NULL,
    EMPTY_STRING: emtpyString,
    STATUS_200,
    LOGOUT,
    ZERO,
    REQUESTED_RECORD,
    ONE,
    TWO,
    STATUS_401
} = constant;

const AllService = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const page = location.state?.page || ZERO;

    const [data, setData] = useState([]);
    const [totalPages, setTotalPages] = useState(ONE);
    const [currentPage, setCurrentPage] = useState(page);
    const [updateModal, setUpdateModal] = useState(falseValue);
    const [createmodal, setCreatemodal] = useState(falseValue);
    const [confirmationModal, setConfirmationModal] = useState(falseValue);
    const [serviceID, setServiceID] = useState(emtpyString);
    const [loading, setLoading] = useState(falseValue);
    const [disable, setDisable] = useState(falseValue);
    const modalRef = useRef(NULL);
    const [createFormData, setCreateFormData] = useState({
        category: emtpyString,
        service: emtpyString,
        priority: emtpyString,
        status: emtpyString
    });
    const [updateFormData, setUpdateFormData] = useState({});

    // * get token from local storage
    const token = localStorage.getItem('vendor_super_token');

    // * execute all service request api
    const executeAllServiceApi = useCallback(async () => {
        try {
            const apiUrl = config.appBaseUrl + ALL_SERVICE_END_POINT;

            // * execute get request
            const response = await axios.get(apiUrl, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            // * api response
            const result = response.data;

            // * if response is success
            if (result.code === STATUS_200 && result.status) {
                setData(result.data);
                setTotalPages(Math.ceil(result.length / REQUESTED_RECORD));
            }
        } catch (error) {
            toast.error(error.response?.data?.message);

            // * if token is expired
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        }
    }, [navigate, token]);

    // * execute all service request api
    useEffect(() => {
        executeAllServiceApi();
    }, [executeAllServiceApi, token, page]);

    // * set total pages
    useEffect(() => {
        setTotalPages(Math.ceil(data.length / REQUESTED_RECORD));
    }, [data]);

    // * paginated data
    const paginatedData = data.slice(
        currentPage * REQUESTED_RECORD,
        (currentPage + ONE) * REQUESTED_RECORD
    );

    // * handle page change
    const handlePageChange = ({ selected }) => {
        setCurrentPage(selected);
        navigate('/service/all-service', { state: { page: selected } });
    };

    //* handle click outside
    const handleClickOutside = (event) => {
        if (modalRef.current && !modalRef.current.contains(event.target)) {
            setUpdateModal(falseValue);
            setCreatemodal(falseValue);
            setConfirmationModal(falseValue);
        }
    };

    //* handle click outside
    useEffect(() => {
        if (updateModal || createmodal || confirmationModal) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [updateModal, createmodal, confirmationModal]);

    // * handle modal open and close
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Tab' && (updateModal || createmodal || confirmationModal)) {
                const focusableElements = document.querySelectorAll(
                    '.modal input, .modal button, .modal select, .modal textarea, .modal a'
                );
                const firstElement = focusableElements[0];
                const lastElement = focusableElements[focusableElements.length - 1];

                //* prevent tabbing outside the modal
                if (event.shiftKey) {
                    //* shift + tab (focus previous element)
                    if (document.activeElement === firstElement) {
                        lastElement.focus();  //* loop to the last element
                        event.preventDefault();
                    }
                } else {
                    //* tab (focus next element)
                    if (document.activeElement === lastElement) {
                        firstElement.focus();  //* loop to the first element
                        event.preventDefault();
                    }
                }
            }
        };

        //* check when modal is open
        if (updateModal || confirmationModal || createmodal) {
            document.body.classList.add('modal-open');
            document.addEventListener('keydown', handleKeyDown);
            //* focus the first element inside the modal when it opens
            const firstFocusable = document.querySelector('.modal button, .modal input, .modal select, .modal a');
            if (firstFocusable) {
                firstFocusable.focus();
            }
        } else {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.body.classList.remove('modal-open');
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [updateModal, createmodal, confirmationModal]);

    // * execute create service
    const executeCreateServiceApi = async (event) => {
        event.preventDefault();

        //* category validation
        if (!createFormData.category) {
            toast.error('Category is required');
            return;
        }

        //* first name validation
        if (!createFormData.service) {
            toast.error('Service is required');
            return;
        }

        //* priority validation
        if (!createFormData.priority) {
            toast.error('Priority is required');
            return;
        }

        //* status validation
        if (createFormData.status === emtpyString) {
            toast.error('Status is required');
            return;
        }

        setLoading(trueValue);
        setDisable(trueValue);

        //* send data to request
        const data = {
            category_type: createFormData.category,
            name: createFormData.service,
            status: createFormData.status,
            priority: Number(createFormData.priority)
        }

        try {
            const apiUrl = config.appBaseUrl + ALL_SERVICE_END_POINT;

            //* post request
            const response = await axios.post(apiUrl,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllServiceApi();
                setCreateFormData({
                    category: emtpyString,
                    service: emtpyString,
                    priority: emtpyString,
                    status: emtpyString
                })
                setCreatemodal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response.data.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    // * handle update 
    const handleUpdateService = (service) => {
        setUpdateFormData({
            id: service.id,
            category: service.category_type,
            service: service.name,
            status: service.status,
            priority: Number(service.priority)
        });
        setUpdateModal(trueValue);
    }

    // * execute update service
    const executeUpdateServiceApi = async (event) => {
        event.preventDefault();

        setLoading(trueValue);
        setDisable(trueValue);

        //* send data to request
        const data = {
            category_type: updateFormData.category,
            name: updateFormData.service,
            status: Number(updateFormData.status),
            priority: Number(updateFormData.priority)
        }

        try {
            const apiUrl = `${config.appBaseUrl + ALL_SERVICE_END_POINT}/${updateFormData.id}`;

            //* post request
            const response = await axios.put(apiUrl,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllServiceApi();
                setUpdateModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    // * execute update service
    const executeDeleteServiceApi = async () => {

        setLoading(trueValue);
        setDisable(trueValue);

        try {
            const apiUrl = `${config.appBaseUrl + ALL_SERVICE_END_POINT}/${serviceID}`;

            //* post request
            const response = await axios.delete(apiUrl,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            //* response success
            const result = response.data;
            if (result.code === STATUS_200 && result.status) {
                toast.success(result.message);
                executeAllServiceApi();
                setConfirmationModal(falseValue);
            } else {
                toast.error(result.message);
            }
        }
        catch (error) {
            toast.error(error.response?.data?.message);

            // * if user is unauthorized
            if (error.response?.data?.code === STATUS_401) {
                navigate(LOGOUT);
            }
        } finally {
            setDisable(falseValue);
            setLoading(falseValue);
        }
    };

    return (
        <>
            <ToastContainer autoClose={700} position='top-center' />

            {/* //* delete service confirmation model */}
            {confirmationModal && (
                <>
                    <div className='modal-backdrop fade show'></div>
                    <div
                        id='user_logout_modal'
                        className='modal fade show'
                        style={{ display: 'block' }}
                    >
                        <div className='modal-dialog'>
                            <div className='modal-content' ref={modalRef}>
                                <div className='modal-body'>
                                    <p className='mb-0'>Are you sure you want to delete this service?</p>
                                </div>
                                <div className='modal-footer me-2 border-top-0'>
                                    <button
                                        type='button'
                                        className='btn btn-danger text-white'
                                        data-dismiss='modal'
                                        onClick={() => setConfirmationModal(falseValue)}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        className='btn btn-success text-white'
                                        onClick={executeDeleteServiceApi}
                                        disabled={disable}
                                    >
                                        {!loading ? 'Confirm' : <Loader />}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}


            {/* //* create service modal */}
            {
                createmodal && (
                    <>
                        <div className='modal-backdrop show'></div>
                        <div
                            id='milestone_modal'
                            className='modal show'
                            tabIndex={-1}
                            role='dialog'
                            aria-labelledby='standard-modalLabel'
                            aria-modal='true'
                            style={{ display: 'block', paddingLeft: 0 }}
                        >
                            <div className='modal-dialog modal-dialog-centered modal-md'>
                                <div className='modal-content' ref={modalRef}>
                                    <div className='modal-header'>
                                        <div className='vendor-bg-grey d-flex align-items-center justify-content-center w-100 py-2 px-2'>
                                            <h5 className='modal-title'>Create Service</h5>
                                            <button
                                                type='button'
                                                className='btn-close vendor-icon'
                                                onClick={() => setCreatemodal(falseValue)}
                                            />
                                        </div>
                                    </div>
                                    <div className='modal-body pt-0'>
                                        <form>
                                            <div className='row'>
                                                <div className='col-md-6 mb-2'>
                                                    <label htmlFor='category' className='form-label'>Category</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='category'
                                                        value={createFormData.category}
                                                        onChange={(e) => setCreateFormData({ ...createFormData, category: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Category</option>
                                                        <option value={1}>Mobile Bar</option>
                                                        <option value={2}>Caterer</option>
                                                    </select>
                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='service' className='form-label'>Service</label>
                                                        <input
                                                            type='text'
                                                            className='form-control m-0'
                                                            id='service'
                                                            placeholder='Enter service'
                                                            value={createFormData.service}
                                                            onChange={(e) => setCreateFormData({ ...createFormData, service: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='priority' className='form-label'>Priority</label>
                                                        <input
                                                            type='number'
                                                            className='form-control m-0'
                                                            id='priority'
                                                            min={1}
                                                            placeholder='Enter priority'
                                                            value={createFormData.priority}
                                                            onChange={(e) => setCreateFormData({ ...createFormData, priority: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <label htmlFor='status_active_inactive' className='form-label'>Status</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='status_active_inactive'
                                                        value={createFormData.status}
                                                        onChange={(e) => setCreateFormData({ ...createFormData, status: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Status</option>
                                                        <option value={1}>Active</option>
                                                        <option value={0}>Inactive</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className='modal-footer pb-0'>
                                                <button
                                                    type='button'
                                                    className='btn btn-danger text-white mb-0'
                                                    onClick={() => setCreatemodal(falseValue)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type='button'
                                                    className='btn btn-primary me-0 mb-0'
                                                    id='create_service'
                                                    onClick={executeCreateServiceApi}
                                                    disabled={disable}
                                                >
                                                    {!loading ? 'Add' : <Loader />}
                                                </button>
                                            </div>
                                        </form>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }

            {/* //* update service modal */}
            {
                updateModal && (
                    <>
                        <div className='modal-backdrop show'></div>
                        <div
                            id='milestone_modal'
                            className='modal show'
                            tabIndex={-1}
                            role='dialog'
                            aria-labelledby='standard-modalLabel'
                            aria-modal='true'
                            style={{ display: 'block', paddingLeft: 0 }}
                        >
                            <div className='modal-dialog modal-dialog-centered modal-md'>
                                <div className='modal-content' ref={modalRef}>
                                    <div className='modal-header'>
                                        <div className='vendor-bg-grey d-flex align-items-center justify-content-center w-100 py-2 px-2'>
                                            <h5 className='modal-title'>Update Service</h5>
                                            <button
                                                type='button'
                                                className='btn-close vendor-icon'
                                                onClick={() => setUpdateModal(falseValue)}
                                            />
                                        </div>
                                    </div>
                                    <div className='modal-body pt-0'>
                                        <form>
                                            <div className='row'>
                                                <div className='col-md-6 mb-2'>
                                                    <label htmlFor='category' className='form-label'>Category</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='category'
                                                        value={updateFormData.category}
                                                        onChange={(e) => setUpdateFormData({ ...updateFormData, category: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Category</option>
                                                        <option value={1}>Mobile Bar</option>
                                                        <option value={2}>Caterer</option>
                                                    </select>

                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='service' className='form-label'>Service</label>
                                                        <input
                                                            type='text'
                                                            className='form-control m-0'
                                                            id='service'
                                                            placeholder='Enter service'
                                                            value={updateFormData.service}
                                                            onChange={(e) => setUpdateFormData({ ...updateFormData, service: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <div className='mb-2'>
                                                        <label htmlFor='priority' className='form-label'>Priority</label>
                                                        <input
                                                            type='number'
                                                            className='form-control m-0'
                                                            id='priority'
                                                            min={1}
                                                            value={updateFormData.priority}
                                                            onChange={(e) => setUpdateFormData({ ...updateFormData, priority: e.target.value })}
                                                        />
                                                    </div>
                                                </div>

                                                <div className='col-md-6 mb-2'>
                                                    <label htmlFor='status_active_inactive' className='form-label'>Status</label>
                                                    <select
                                                        className='form-control m-0'
                                                        id='status_active_inactive'
                                                        value={updateFormData.status}
                                                        onChange={(e) => setUpdateFormData({ ...updateFormData, status: Number(e.target.value) })}
                                                    >
                                                        <option value=''>Select Status</option>
                                                        <option value={1}>Active</option>
                                                        <option value={0}>Inactive</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div className='modal-footer pb-0'>
                                                <button
                                                    type='button'
                                                    className='btn btn-danger text-white mb-0'
                                                    onClick={() => setUpdateModal(falseValue)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type='button'
                                                    className='btn btn-primary me-0 mb-0'
                                                    id='create_service'
                                                    onClick={executeUpdateServiceApi}
                                                    disabled={disable}
                                                >
                                                    {!loading ? 'Update' : <Loader />}
                                                </button>
                                            </div>
                                        </form>

                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )
            }

            <div className='page-content'>
                <div className='container-fluid'>
                    {/* //* start page title */}
                    <div className='row'>
                        <div className='col-12'>
                            <div className='page-title-box d-flex  align-items-start align-items-sm-center justify-content-between gap-2'>
                                <h4 className='mb-2 mb-sm-0'>Services</h4>
                                <div className='page-title-right'>
                                    <ol className='breadcrumb m-0'>
                                        <li className='breadcrumb-item'>
                                            <Link to='/dashboard'>Dashboard</Link>
                                        </li>
                                        <li className='breadcrumb-item active'>Services</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='row'>
                        <div className='col-lg-12'>
                            <div className='card'>
                                <div className='card-body'>
                                    {/* //* header buttons */}
                                    <div
                                        className='d-flex align-items-center justify-content-end mb-3'
                                        style={{ width: '100%' }}
                                    >
                                        <button type='button' className='btn btn-success' onClick={() => setCreatemodal(!createmodal)}>
                                            Add
                                        </button>
                                    </div>

                                    <div className='table-responsive'>
                                        <table className='w-100 text-sm text-left border-collapse custom-stripe-table '>
                                            <thead className='bg-gray-200  text-center'>
                                                <tr>
                                                    <th className='px-4 py-2'>SN</th>
                                                    <th className='px-4 py-2'>Category</th>
                                                    <th className='px-4 py-2'>Service</th>
                                                    <th className='px-4 py-2'>Priority</th>
                                                    <th className='px-4 py-2'>Status</th>
                                                    <th className='px-4 py-2'>Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {
                                                    paginatedData.length > ZERO ?
                                                        paginatedData.map((service, index) => (
                                                            <tr className='bg-white hover:bg-gray-100' key={service.id}>
                                                                <td className='px-4 py-2'>{currentPage * REQUESTED_RECORD + (index + ONE)}</td>
                                                                <td className='px-4 py-2'>{service.category_type === ONE ? 'Mobile Bar' : 'Caterer'}</td>
                                                                <td className='px-4 py-2 text-nowrap'>{service.name}</td>
                                                                <td className='px-4 py-2'>{service.priority}</td>
                                                                <td className='px-4 py-2'>
                                                                    <span className={`badge ${service.status === ZERO ? 'badge-danger-lighten' : 'badge-success-lighten'}`}>{service.status === ZERO ? 'INACTIVE' : 'ACTIVE'}</span>
                                                                </td>
                                                                <td className='px-4 py-2'>
                                                                    <div className='d-flex align-items-center justify-content-center'>
                                                                        <RiPencilFill
                                                                            size={20}
                                                                            data-tooltip-id='update_service' data-tooltip-content='Update Service'
                                                                            className=' text-primary vendor-icon pointer user-status me-2'
                                                                            onClick={() => handleUpdateService(service)}
                                                                        />
                                                                        <Tooltip id='update_service' place='bottom' effect='solid' />

                                                                        <MdDelete
                                                                            size={20}
                                                                            data-tooltip-id='delele_service' data-tooltip-content='Delete Service'
                                                                            className=' text-danger vendor-icon pointer user-status'
                                                                            onClick={() => {
                                                                                setConfirmationModal(trueValue);
                                                                                setServiceID(service.id)
                                                                            }}
                                                                        />
                                                                        <Tooltip id='delele_service' place='bottom' effect='solid' />
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))
                                                        :
                                                        <tr>
                                                            <td colSpan={6}>No data found!</td>
                                                        </tr>
                                                }
                                            </tbody>
                                        </table>
                                    </div>

                                    {/* //* pagination */}
                                    {
                                        paginatedData.length > ZERO &&
                                        <div className='float-end'>
                                            <ReactPaginate
                                                previousLabel={<MdKeyboardArrowLeft className='fs-4' />}
                                                nextLabel={<MdKeyboardArrowRight className='fs-4' />}
                                                className='pagination'
                                                breakLabel={'...'}
                                                breakLinkClassName={'disabled-break'}
                                                pageCount={totalPages > ZERO ? totalPages : ONE}
                                                forcePage={(totalPages > ZERO) && (currentPage < totalPages) ? (currentPage) : ZERO}
                                                marginPagesDisplayed={TWO}
                                                pageRangeDisplayed={TWO}
                                                onPageChange={handlePageChange}
                                                containerClassName={'pagination'}
                                                subContainerClassName={'pages pagination'}
                                                activeClassName={'pagination-active-page'}
                                            />
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default AllService;